import React from "react";
import { Box, HStack, Stack, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { FAMILIES, familyColor, useVizTokens } from "./tokens";
import { axisMoney, count, money } from "./format";

// What texting cost each day, week or month, stacked by why the texts went out (FAMILIES, bottom
// to top). The legend names every family with its total; hovering a column lists them all.

const GAP = 2;

function topFamily(row) {
  for (let i = FAMILIES.length - 1; i >= 0; i -= 1) {
    if (row[FAMILIES[i].key] > 0) return FAMILIES[i].key;
  }
  return null;
}

// One stacked segment: a 2px surface gap above every segment but the top one, whose data-end is
// rounded (4px); square at the baseline.
function segmentShape(familyKey) {
  return function Segment({ x, y, width, height, fill, payload }) {
    if (!height || height <= 0 || !width) return null;
    const top = topFamily(payload || {}) === familyKey;
    const gap = top ? 0 : Math.min(GAP, height / 2);
    const y0 = y + gap;
    const h = height - gap;
    const r = top ? Math.min(4, width / 2, h) : 0;
    const d = r
      ? `M${x},${y0 + h} L${x},${y0 + r} Q${x},${y0} ${x + r},${y0} L${x + width - r},${y0} `
        + `Q${x + width},${y0} ${x + width},${y0 + r} L${x + width},${y0 + h} Z`
      : `M${x},${y0} h${width} v${h} h${-width} Z`;
    return <path d={d} fill={fill} />;
  };
}

function SpendTooltip({ active, payload, tokens }) {
  const row = active && payload?.[0]?.payload;
  if (!row) return null;
  const parts = FAMILIES.filter((f) => row[f.key] > 0).reverse();
  return (
    <Box bg={tokens.surface} borderWidth="1px" borderColor={tokens.border} borderRadius="md" px={3} py={2}
         boxShadow="md" fontSize="xs" minW="220px">
      <Text color={tokens.textSecondary}>{row.label}</Text>
      <Text color={tokens.text} fontWeight="semibold" fontSize="sm" mb={1}>
        {money(row.cost)} · {count(row.texts)} texts · {count(row.segments)} segments
      </Text>
      <Stack spacing={0.5}>
        {parts.map((f) => (
          <HStack key={f.key} spacing={2}>
            <Box w="12px" h="2px" bg={familyColor(tokens, f.key)} flexShrink={0} />
            <Text color={tokens.text} fontWeight="semibold" minW="52px" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {money(row[f.key])}
            </Text>
            <Text color={tokens.textSecondary}>{f.label}</Text>
          </HStack>
        ))}
      </Stack>
    </Box>
  );
}

export default function SpendOverTime({ rows, totals, height = 260 }) {
  const t = useVizTokens();
  return (
    <Box>
      <Wrap spacing={4} mb={3}>
        {FAMILIES.map((f) => (
          <WrapItem key={f.key}>
            <HStack spacing={1.5} fontSize="xs">
              <Box w="10px" h="10px" borderRadius="2px" bg={familyColor(t, f.key)} />
              <Text color={t.textSecondary}>{f.label}</Text>
              {totals && <Text color={t.text} fontWeight="semibold">{money(totals[f.key] || 0)}</Text>}
            </HStack>
          </WrapItem>
        ))}
      </Wrap>
      <Box h={`${height}px`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap="20%">
            <CartesianGrid vertical={false} stroke={t.grid} />
            <XAxis dataKey="label" tick={{ fill: t.muted, fontSize: 11 }} tickLine={false}
                   axisLine={{ stroke: t.baseline }} interval="preserveStartEnd" minTickGap={18} />
            <YAxis tickFormatter={axisMoney} tick={{ fill: t.muted, fontSize: 11 }} tickLine={false}
                   axisLine={false} width={48} allowDecimals />
            <Tooltip cursor={{ fill: t.hover }} content={<SpendTooltip tokens={t} />} />
            {FAMILIES.map((f) => (
              <Bar key={f.key} dataKey={f.key} name={f.label} stackId="spend" fill={familyColor(t, f.key)}
                   maxBarSize={24} isAnimationActive={false} shape={segmentShape(f.key)} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
